/*
 * Copyright (c) Nexign, JSC, 2020-2024.
 */
/**
 * InquiryState component
 *
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.28.0
 * @since 1.3.0
 */

import { Fragment, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { EInquiryTableType } from '@/main/pages/PageHistoryInquiry/types';
import { icons } from '@assets/index';
import { Button } from '@components/common';
import { ButtonImageStyled } from '@components/common/Button/styled/ButtonStyled';

import { useInquiryStateRequest } from '../hooks/useInquiryStateRequest';
import { useInquirySyncRequest } from '../hooks/useInquirySyncRequest';
import { EInquiryStates } from '../inquiryStateTypes';
import { inquiryStateApi } from '../store';

type TInquiryStateProps = {
    inquiryTableType: EInquiryTableType;
};

const InquiryState = ({ inquiryTableType }: TInquiryStateProps) => {
    const isDefaultInquiryTableType = inquiryTableType === EInquiryTableType.DEFAULT;

    const dispatch = useDispatch();

    const [pollingInterval, setPollingInterval] = useState(5000);

    const [getInquiryState, inquiryStateResult] = useInquiryStateRequest({ inquiryTableType, pollingInterval });

    const [postInquirySync] = useInquirySyncRequest({ inquiryTableType });

    useEffect(() => {
        if (inquiryStateResult.data?.currentState === EInquiryStates.IS_DONE || inquiryStateResult.error) {
            setPollingInterval(0);
        } else {
            getInquiryState();
        }
    }, [inquiryStateResult]);

    const refreshStateHandler = () => {
        if (inquiryStateResult.data?.currentState === EInquiryStates.IS_DONE) {
            postInquirySync();
            setPollingInterval(5000);
            dispatch(
                inquiryStateApi.util.updateQueryData(
                    isDefaultInquiryTableType ? 'getInquiryState' : 'getOtherInquiryState',
                    undefined,
                    (draft) => {
                        draft.currentState = EInquiryStates.WORKING;
                        draft.progressPercents = 0;
                    },
                ),
            );
        } else {
            getInquiryState();
        }
    };

    return (
        <Button className={'page-title__refresh-button'} dataName="refresh" kind="circle" onClick={refreshStateHandler}>
            {inquiryStateResult.data?.currentState === EInquiryStates.WORKING ? (
                <Fragment>{inquiryStateResult.data?.progressPercents || 0} %</Fragment>
            ) : (
                <ButtonImageStyled alt="refresh-icon" src={icons.refreshIcon}></ButtonImageStyled>
            )}
        </Button>
    );
};

export default InquiryState;
